import { Rfx } from "newui/features/Rfx/types/rfx.types";
import {
	AccountSettingsPaths,
	MySuppliersPaths,
	RfxClientPaths,
	RfxProjectPaths,
	SupplierListPaths,
	BaseMemberPaths,
} from "./navigation.types";
import { getSlug } from "helpers/linkHelper";
import { RfxTab } from "newui/features/Rfx/types/component-types";
import {
	RFX_DEFAULT_TAB,
	RFX_SCOPE_ROUTE_SEGMENTS,
} from "newui/features/Rfx/tabs/rfx-tab-config";

const rfxProjectBase = "projects";
const supplierListBase = "supplier-list";

export class ClientPaths extends BaseMemberPaths {
	dashboard: string;
	rfxProjects: RfxProjectPaths;
	companies: string;
	supplierSearch: string;
	supplierList: SupplierListPaths;
	mySuppliers: MySuppliersPaths;

	rfx: RfxClientPaths;

	constructor(companyId: number, hasDashboard: boolean, hasRfx: boolean) {
		super(companyId);

		this.dashboard = this.prefixWithBasePath("dashboard");
		this.companies = this.prefixWithBasePath("companies");
		this.rfxProjects = {
			all: this.prefixWithBasePath(`${rfxProjectBase}?tab=allProject`),
			archived: this.prefixWithBasePath(`${rfxProjectBase}?tab=archived`),
			favourites: this.prefixWithBasePath(`${rfxProjectBase}?tab=favourite`),
			my: this.prefixWithBasePath(rfxProjectBase),
			new: this.prefixWithBasePath(`${rfxProjectBase}/new-project`),
		};
		this.supplierSearch = this.prefixWithBasePath("search");

		this.supplierList = {
			my: this.prefixWithBasePath(supplierListBase),
			new: this.prefixWithBasePath(
				this.joinPaths(supplierListBase, "new-supplier-list")
			),
			panel: this.prefixWithBasePath(supplierListBase),
		};

		this.mySuppliers = {
			list: this.prefixWithBasePath("my-suppliers"),
			rate: this.prefixWithBasePath("my-suppliers/rate"),
		};

		this.rfx = {
			all: this.prefixWithBasePath("rfx"),
			tendersQuotes: this.prefixWithBasePath(
				`rfx/${RFX_SCOPE_ROUTE_SEGMENTS.tenders_quotes}/${RFX_DEFAULT_TAB}`
			),
			tendersQuotesTab: (tab) =>
				this.prefixWithBasePath(
					`rfx/${RFX_SCOPE_ROUTE_SEGMENTS.tenders_quotes}/${tab}`
				),
			approvals: this.prefixWithBasePath(
				`rfx/${RFX_SCOPE_ROUTE_SEGMENTS.approvals}/${RFX_DEFAULT_TAB}`
			),
			panel: this.prefixWithBasePath(
				`rfx/${RFX_SCOPE_ROUTE_SEGMENTS.panel}/${RFX_DEFAULT_TAB}`
			),
			createTemplate: this.prefixWithBasePath("rfx-template/new"),
			manageTemplate: this.prefixWithBasePath("manage-templates"),
		};

		if (hasDashboard) {
			this.home = this.dashboard;
		} else if (hasRfx) {
			this.home = this.rfxProjects.all;
		} else {
			this.home = this.supplierSearch;
		}
	}

	// ---------------------------
	// RFx Project Helpers
	// ---------------------------

	rfxProjectsWithType(typeSlug?: string | null) {
		if (typeSlug) {
			return `${this.rfxProjects.my}?type=${typeSlug}`;
		}
		return this.rfxProjects.my;
	}

	newRfxProject(typeSlug?: string | null) {
		if (typeSlug) {
			return `${this.rfxProjects.new}?type=${typeSlug}`;
		}
		return this.rfxProjects.new;
	}

	editRfxProject(rfxProjectId: number) {
		return this.prefixWithBasePath(
			this.joinPaths(rfxProjectBase, `/edit-project/${rfxProjectId}`)
		);
	}

	editRfxTemplate(rfxTemplateId: number) {
		return this.prefixWithBasePath(`rfx-template/${rfxTemplateId}`);
	}

	newRfxProjectWithSupplierList(supplierListId: number) {
		return this.joinPaths(
			this.rfxProjects.new,
			`/suppliers-list/${supplierListId}`
		);
	}

	viewRfxProject(rfxProjectId: number, rfxProjectName: string) {
		const slug = getSlug(rfxProjectName);
		return this.joinPaths(
			this.prefixWithBasePath(rfxProjectBase),
			`/${rfxProjectId}/${slug}`
		);
	}

	// ---------------------------
	// RFx Helpers
	// ---------------------------

	createRfx(rfxId: number) {
		return this.prefixWithBasePath(`rfx/${rfxId}/create-rfx`);
	}

	editRfx(rfxId: number) {
		return this.prefixWithBasePath(`rfx/${rfxId}/edit-rfx`);
	}

	/**
	 * redirects to /account/123/rfx/456/SLUG/OPTIONAL_TAB or All
	 * used to view the entire RFx - with all invites etc.
	 * @param rfxData
	 * @returns
	 */
	viewRfx(rfxData: Rfx, tabId: RfxTab = null, contextId: number | null = null) {
		const slug = getSlug(rfxData?.name);
		const basePath = this.prefixWithBasePath(`rfx/${rfxData?.id}/${slug}`);

		if (!tabId) {
			return `${basePath}/all`;
		}

		return `${basePath}/${tabId}/${contextId ? contextId : ""}`;
	}

	/**
	 * redirects to /account/123/rfx/view
	 * used to view the RFx how a supplier would see it
	 * @param rfxId
	 * @returns
	 */
	viewRfxDetails(rfxId: number) {
		return this.prefixWithBasePath(`rfx/${rfxId}/view`);
	}

	/**
	 * Same target as `viewRfx`'s default ("all") branch, for callers that only
	 * have the lean `RfxListItemResource` row (`rfx_id`/`name`), not the full
	 * `Rfx`. Used by Approvals rows — the pending pre-approval action and the
	 * Past read view are both the RFx detail page itself (006's mail already
	 * lands the approver on Approvals → Current; the row click then drills
	 * into this same detail target).
	 */
	viewApprovalRfx(rfxId: number, rfxName: string | null) {
		const slug = getSlug(rfxName ?? "");
		return this.prefixWithBasePath(`rfx/${rfxId}/${slug}/all`);
	}

	viewEvaluation(rfxId: number, evaluationId: number) {
		return this.prefixWithBasePath(`rfx/${rfxId}/evaluation/${evaluationId}`);
	}

	/**
	 * Read-only preview (uuid-addressed, per TASK-011's `rfq/{uuid}/preview`
	 * route) — used when a Panel row's access flags are not yet an active
	 * scorer (no `rfx_evaluation_id`), or as the fallback target so a row
	 * click never constructs a broken `/evaluation/null` URL.
	 */
	viewRfxPreview(rfxUuid: string) {
		return this.prefixWithBasePath(`rfx/${rfxUuid}/preview`);
	}

	viewRfxTemplate(rfxTemplateId: number) {
		return this.prefixWithBasePath(`rfx-template/${rfxTemplateId}`);
	}

	/**
	 *
	 * @param supplierListId The id of the supplier list
	 * @param supplierListName the name of the supplier list (for url slug)
	 * @returns
	 */
	viewSupplierList(supplierListId: number, supplierListName: string) {
		const slug = getSlug(supplierListName);
		return this.joinPaths(
			this.prefixWithBasePath(supplierListBase),
			`/${supplierListId}/${slug}`
		);
	}

	// ---------------------------
	// My Suppliers Helpers
	// ---------------------------

	viewSupplierDetail(supplierId: number) {
		return this.joinPaths(this.mySuppliers.list, `/${supplierId}`);
	}

	rateSupplier(supplierId: number) {
		return this.joinPaths(this.mySuppliers.rate, `/${supplierId}`);
	}
}
